import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { converterCategories, getCategory } from "../data/tools";
import { ConverterWidget } from "../components/ConverterWidget";
import { AdSlot } from "../components/AdSlot";
import { SectionLabel } from "../components/SectionLabel";
import { usePageMeta } from "../lib/usePageMeta";
import { trackUsage } from "../lib/api";
import NotFound from "./NotFound";

export default function ConverterCategory() {
  const { categoryId } = useParams();
  const category = getCategory(categoryId);

  usePageMeta(
    category ? `${category.label} Converter — Free & Instant | Utiliverse` : "Converter | Utiliverse",
    category ? `Convert ${category.label.toLowerCase()} units instantly. ${category.blurb}` : ""
  );

  useEffect(() => {
    if (category) {
      trackUsage({
        slug: `conv-${category.id}`,
        title: `${category.label} Converter`,
        path: `/converters/${category.id}`,
        category: "converter",
      });
    }
  }, [category]);

  if (!category) return <NotFound />;

  // build common pair links (each unit -> next few units)
  const pairs = [];
  category.units.forEach((from) => {
    category.units.forEach((to) => {
      if (from.id !== to.id) pairs.push({ from, to });
    });
  });

  return (
    <div>
      <section className="bp-grid border-b-2 border-ink">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-12">
          <nav className="font-mono text-xs text-ink/50 mb-4">
            <Link to="/converters" className="hover:text-signal">Converters</Link> / {category.label}
          </nav>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mb-8">{category.label} Converter</h1>
          <ConverterWidget category={category} />
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-12">
        <AdSlot format="leaderboard" testid="category-ad" />

        <section className="mt-12">
          <SectionLabel n="◆">All {category.label} Conversions</SectionLabel>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 border-t-2 border-l-2 border-ink">
            {pairs.map(({ from, to }) => (
              <Link
                key={`${from.id}-${to.id}`}
                to={`/convert/${category.id}/${from.id}/${to.id}`}
                data-testid={`pair-${from.id}-${to.id}`}
                className="border-b-2 border-r-2 border-ink bg-paper px-5 py-4 hover:bg-gold transition-colors flex items-center justify-between group"
              >
                <span className="text-sm font-medium">
                  {from.name} <span className="text-ink/40">to</span> {to.name}
                </span>
                <span className="font-mono text-xs text-ink/40 group-hover:text-ink">{from.symbol}→{to.symbol}</span>
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-14">
          <SectionLabel n="◆">Other Converters</SectionLabel>
          <div className="flex flex-wrap gap-2">
            {converterCategories.filter((c) => c.id !== category.id).map((c) => (
              <Link key={c.id} to={`/converters/${c.id}`} className="font-mono text-xs border-2 border-ink bg-paper px-3 py-2 hover:bg-canvas">
                {c.label}
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
